import AsyncStorage from "@react-native-async-storage/async-storage";

import { config } from "../shared/environment";
import {
    CUSTOM_LAYOUT_FIELDS,
    CustomLabelLayout,
    CustomLayoutNumericKey,
    DEFAULT_CUSTOM_LABEL_LAYOUT,
    DEFAULT_PRODUCT_SETTINGS,
    LABEL_FIELD_KEYS,
    LABEL_LAYOUT_KEYS,
    LabelLayoutKey,
    PRODUCT_SETTINGS_STORAGE_KEY,
    PRODUCT_SETTINGS_VERSION,
    ProductLike,
    ProductSettings,
    SAMPLE_PREVIEW_PRODUCT,
    SEPARATOR_OPTIONS,
    SKU_COUNTER_STORAGE_KEY,
    SKU_PREFIX_MAX_LENGTH,
    SKU_SEQUENCE_MAX_LENGTH,
    SKU_SEQUENCE_MIN_LENGTH,
    SKU_START_NUMBER_MAX,
    SKU_START_NUMBER_MIN,
    SKU_TOKEN_LENGTH,
    SKU_TOKEN_OPTIONS,
    SkuSeparator,
} from "../components/settings/productSettings.module";

/**
 * Keys are resolved lazily: config.storeId reads the redux store, which is only
 * populated after sign in, so building the key at module load would bind it to
 * `undefined` for the whole session.
 */
const settingsKey = () => `${PRODUCT_SETTINGS_STORAGE_KEY}_${config.storeId}`;
const counterKey = () => `${SKU_COUNTER_STORAGE_KEY}_${config.storeId}`;

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const toNumber = (value: unknown, fallback: number): number => {
    const parsed = typeof value === "number" ? value : parseInt(String(value ?? ""), 10);
    return Number.isFinite(parsed) ? parsed : fallback;
};

const toBoolean = (value: unknown, fallback: boolean): boolean =>
    typeof value === "boolean" ? value : fallback;

const isSeparator = (value: unknown): value is SkuSeparator =>
    SEPARATOR_OPTIONS.some((option) => option.value === value);

/** Strips non alphanumerics, uppercases and trims to the configured length. */
export const sanitizePrefix = (value: unknown): string =>
    String(value ?? "")
        .replace(/[^a-zA-Z0-9]/g, "")
        .toUpperCase()
        .slice(0, SKU_PREFIX_MAX_LENGTH);

/** "Cosmetics" -> "COS". Returns "" when the source has no usable characters. */
export const toTokenCode = (value: unknown): string =>
    String(value ?? "")
        .replace(/[^a-zA-Z0-9]/g, "")
        .toUpperCase()
        .slice(0, SKU_TOKEN_LENGTH);

/**
 * Clamps every Custom layout dimension into the range its field declares, so a
 * hand edited or older payload can never produce a page the renderer chokes on.
 */
export const mergeCustomLayout = (raw: unknown): CustomLabelLayout => {
    const stored = (raw && typeof raw === "object" ? raw : {}) as Partial<CustomLabelLayout>;

    const merged = CUSTOM_LAYOUT_FIELDS.reduce(
        (result, field) => {
            const fallback = DEFAULT_CUSTOM_LABEL_LAYOUT[field.key];
            const value = toNumber(stored[field.key], fallback);
            const clamped = clamp(value, field.min, field.max);
            result[field.key] = field.allowDecimal ? clamped : Math.round(clamped);
            return result;
        },
        {} as Record<CustomLayoutNumericKey, number>,
    );

    return {
        ...DEFAULT_CUSTOM_LABEL_LAYOUT,
        ...merged,
        showCutGuides: toBoolean(stored.showCutGuides, DEFAULT_CUSTOM_LABEL_LAYOUT.showCutGuides),
    };
};

/**
 * Merges a stored payload onto the defaults field by field, so a settings blob
 * written by an older build (missing keys, wrong types, out of range numbers)
 * can never crash the app or produce an unusable SKU.
 */
export const mergeWithDefaults = (raw: unknown): ProductSettings => {
    const stored = (raw && typeof raw === "object" ? raw : {}) as Partial<ProductSettings>;
    const storedSku = (stored.sku && typeof stored.sku === "object" ? stored.sku : {}) as Partial<
        ProductSettings["sku"]
    >;
    const storedLabel = (stored.label && typeof stored.label === "object" ? stored.label : {}) as Partial<
        ProductSettings["label"]
    >;

    // Unknown / duplicate field keys are dropped; the stored order is preserved.
    const fields = Array.isArray(storedLabel.fields)
        ? storedLabel.fields.filter(
            (field, index, list) => LABEL_FIELD_KEYS.includes(field) && list.indexOf(field) === index,
        )
        : [];

    return {
        version: PRODUCT_SETTINGS_VERSION,
        sku: {
            enabled: toBoolean(storedSku.enabled, DEFAULT_PRODUCT_SETTINGS.sku.enabled),
            prefix: sanitizePrefix(storedSku.prefix ?? DEFAULT_PRODUCT_SETTINGS.sku.prefix),
            separator: isSeparator(storedSku.separator)
                ? storedSku.separator
                : DEFAULT_PRODUCT_SETTINGS.sku.separator,
            includeCategory: toBoolean(storedSku.includeCategory, DEFAULT_PRODUCT_SETTINGS.sku.includeCategory),
            includeBrand: toBoolean(storedSku.includeBrand, DEFAULT_PRODUCT_SETTINGS.sku.includeBrand),
            includeVariant: toBoolean(storedSku.includeVariant, DEFAULT_PRODUCT_SETTINGS.sku.includeVariant),
            sequenceLength: clamp(
                toNumber(storedSku.sequenceLength, DEFAULT_PRODUCT_SETTINGS.sku.sequenceLength),
                SKU_SEQUENCE_MIN_LENGTH,
                SKU_SEQUENCE_MAX_LENGTH,
            ),
            startNumber: clamp(
                toNumber(storedSku.startNumber, DEFAULT_PRODUCT_SETTINGS.sku.startNumber),
                SKU_START_NUMBER_MIN,
                SKU_START_NUMBER_MAX,
            ),
        },
        label: {
            // At least one field must always print.
            fields: fields.length > 0 ? fields : [...DEFAULT_PRODUCT_SETTINGS.label.fields],
            layout: LABEL_LAYOUT_KEYS.includes(storedLabel.layout as LabelLayoutKey)
                ? (storedLabel.layout as LabelLayoutKey)
                : DEFAULT_PRODUCT_SETTINGS.label.layout,
            custom: mergeCustomLayout(storedLabel.custom),
        },
    };
};

export const getProductSettings = async (): Promise<ProductSettings> => {
    try {
        const stored = await AsyncStorage.getItem(settingsKey());
        if (!stored) {
            return mergeWithDefaults(null);
        }
        return mergeWithDefaults(JSON.parse(stored));
    } catch {
        // Corrupt JSON or unavailable storage must not block the screen.
        return mergeWithDefaults(null);
    }
};

export const saveProductSettings = async (settings: ProductSettings): Promise<ProductSettings> => {
    const sanitized = mergeWithDefaults(settings);
    await AsyncStorage.setItem(settingsKey(), JSON.stringify(sanitized));
    return sanitized;
};

/** Restores the defaults. The SKU counter lives under its own key and is untouched. */
export const resetProductSettings = async (): Promise<ProductSettings> => {
    const defaults = mergeWithDefaults(null);
    await AsyncStorage.setItem(settingsKey(), JSON.stringify(defaults));
    return defaults;
};

/* ------------------------------------------------------------------ */
/* SKU building                                                       */
/* ------------------------------------------------------------------ */

const padSequence = (value: number, length: number) => String(Math.max(value, 0)).padStart(length, "0");

/**
 * Joins prefix + enabled tokens + padded sequence.
 * Empty segments (e.g. a product with no brand) are dropped so the SKU never
 * contains a doubled separator.
 */
const buildSku = (settings: ProductSettings, product: ProductLike, sequence: number): string => {
    const segments: string[] = [];

    const prefix = sanitizePrefix(settings.sku.prefix);
    if (prefix) {
        segments.push(prefix);
    }

    SKU_TOKEN_OPTIONS.forEach((token) => {
        if (!settings.sku[token.key]) {
            return;
        }
        const code = toTokenCode(token.getSource(product));
        if (code) {
            segments.push(code);
        }
    });

    segments.push(padSequence(sequence, settings.sku.sequenceLength));

    return segments.join(settings.sku.separator);
};

/**
 * Pure preview used by the settings screen. No storage access and no counter
 * increment - the sequence shown is the configured starting number.
 */
export const previewSku = (
    settings: ProductSettings,
    sample: ProductLike = SAMPLE_PREVIEW_PRODUCT,
    sequence?: number,
): string => buildSku(settings, sample, sequence ?? settings.sku.startNumber);

/* ------------------------------------------------------------------ */
/* SKU counter                                                        */
/* ------------------------------------------------------------------ */

/**
 * Serialises counter access. Two rapid getNextSku() calls would otherwise both
 * read the same value before either writes back, handing out a duplicate SKU.
 */
let counterQueue: Promise<unknown> = Promise.resolve();

const enqueue = <T,>(task: () => Promise<T>): Promise<T> => {
    const run = counterQueue.then(task, task);
    // Keep the chain alive whether the task resolved or threw.
    counterQueue = run.then(
        () => undefined,
        () => undefined,
    );
    return run;
};

const readCounter = async (fallback: number): Promise<number> => {
    try {
        const stored = await AsyncStorage.getItem(counterKey());
        if (!stored) {
            return fallback;
        }
        const parsed = JSON.parse(stored);
        const next = toNumber(parsed?.next, fallback);
        return next >= 0 ? next : fallback;
    } catch {
        return fallback;
    }
};

const writeCounter = (next: number) => AsyncStorage.setItem(counterKey(), JSON.stringify({ next }));

/** The number the next generated SKU will use. Read only, does not consume it. */
export const peekNextSkuNumber = async (settings?: ProductSettings): Promise<number> => {
    const resolved = settings ?? (await getProductSettings());
    return enqueue(() => readCounter(resolved.sku.startNumber));
};

/** Sets the counter back to the configured starting number. */
export const resetSkuCounter = async (settings?: ProductSettings): Promise<number> => {
    const resolved = settings ?? (await getProductSettings());
    return enqueue(async () => {
        await writeCounter(resolved.sku.startNumber);
        return resolved.sku.startNumber;
    });
};

export interface NextSkuResult {
    sku: string;
    sequence: number;
}

/**
 * Builds the next SKU and consumes one sequence number.
 * Pass `settings` when the caller already holds them, to avoid a storage read.
 */
export const getNextSku = async (
    product: ProductLike = {},
    settings?: ProductSettings,
): Promise<NextSkuResult> => {
    const resolved = settings ?? (await getProductSettings());
    return enqueue(async () => {
        const sequence = await readCounter(resolved.sku.startNumber);
        const sku = buildSku(resolved, product, sequence);
        await writeCounter(sequence + 1);
        return { sku, sequence };
    });
};

/** Test hook - clears the in-memory serialisation chain between test cases. */
export const __resetSkuQueueForTests = () => {
    counterQueue = Promise.resolve();
};
